<template>
  <div class="integrations-area ptb-100">
    <div class="container">
      <div class="section-title text-center">
        <span class="sub-title">Integrações Poderosas</span>
        <h2>Conecte-se com as ferramentas que você já usa</h2>
        <p>HawkCRM se integra perfeitamente com centenas de aplicativos e serviços</p>
      </div>

      <!-- Categorias de Integração -->
      <div class="integrations-categories" data-aos="fade-up">
        <button
            v-for="category in categories"
            :key="category.id"
            :class="['category-btn', { active: activeCategory === category.id }]"
            @click="activeCategory = category.id"
        >
          <i :class="category.icon"></i>
          {{ category.name }}
        </button>
      </div>

      <!-- Grid de Integrações -->
      <div class="integrations-grid" data-aos="fade-up" data-aos-delay="100">
        <div
            v-for="integration in filteredIntegrations"
            :key="integration.id"
            class="integration-card"
        >
          <div class="integration-logo">
            <img v-if="integration.logo" :src="integration.logo" :alt="integration.name">
            <i v-else :class="integration.icon" :style="{ color: integration.color }"></i>
          </div>
          <h4>{{ integration.name }}</h4>
          <p>{{ integration.description }}</p>
          <div class="integration-badge" v-if="integration.badge">
            {{ integration.badge }}
          </div>
        </div>
      </div>

      <!-- Recursos de Integração -->
      <div class="integration-features">
        <div class="row align-items-center">
          <div class="col-lg-6" data-aos="fade-right">
            <div class="features-content">
              <h3>Integração via API REST Completa</h3>
              <p>Conecte HawkCRM com qualquer sistema usando nossa API REST robusta e bem documentada.</p>
              <ul class="features-list">
                <li>
                  <i class="ph-check-circle"></i>
                  <span>Documentação completa e interativa</span>
                </li>
                <li>
                  <i class="ph-check-circle"></i>
                  <span>Webhooks para notificações em tempo real</span>
                </li>
                <li>
                  <i class="ph-check-circle"></i>
                  <span>Autenticação OAuth2 segura</span>
                </li>
                <li>
                  <i class="ph-check-circle"></i>
                  <span>Rate limiting e logs detalhados</span>
                </li>
                <li>
                  <i class="ph-check-circle"></i>
                  <span>SDKs em várias linguagens</span>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-6" data-aos="fade-left">
            <div class="features-image">
              <div class="code-preview">
                <div class="code-header">
                  <span class="code-lang">API Request</span>
                  <div class="code-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <pre class="code-content"><code>POST /api/customers
                  Authorization: Bearer YOUR_TOKEN

                  {
                    "name": "Nova Empresa LTDA",
                    "email": "contato@empresa.com",
                    "phone": "+55 11 98765-4321",
                    "custom_fields": {
                      "segment": "tecnologia"
                    }
                  }

                  <span class="code-comment">// Response 201 Created</span>
                  {
                    "id": 1234,
                    "status": "active",
                    "created_at": "2025-01-15"
                  }</code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA -->
      <div class="integrations-cta text-center" data-aos="fade-up">
        <h3>Não encontrou a integração que precisa?</h3>
        <p>Nossa equipe pode criar integrações personalizadas para suas necessidades</p>
        <a href="https://wa.me/5554992302401?text=Preciso de uma integração personalizada no HawkCRM"
           target="_blank"
           class="btn-style-one blue-color"
           rel="noopener noreferrer">
          Falar sobre Integração Customizada
          <i class="ph-caret-right"></i>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SoftwareIntegrations',
  data() {
    return {
      activeCategory: 'all',
      categories: [
        { id: 'all', name: 'Todas', icon: 'ph-stack' },
        { id: 'payment', name: 'Pagamentos', icon: 'ph-credit-card' },
        { id: 'communication', name: 'Comunicação', icon: 'ph-chats' },
        { id: 'productivity', name: 'Produtividade', icon: 'ph-briefcase' },
        { id: 'marketing', name: 'Marketing', icon: 'ph-megaphone' },
        { id: 'automation', name: 'Automação', icon: 'ph-robot' }
      ],
      integrations: [
        // PAGAMENTOS
        {
          id: 1,
          category: 'payment',
          name: 'Stripe',
          icon: 'ph-stripe-logo',
          color: '#635BFF',
          description: 'Aceite pagamentos online com cartão de crédito',
          badge: 'Popular'
        },
        {
          id: 2,
          category: 'payment',
          name: 'PayPal',
          logo: 'https://cdn.simpleicons.org/paypal/0070BA',
          description: 'Pagamentos seguros via PayPal',
          badge: null
        },
        {
          id: 3,
          category: 'payment',
          name: 'Mercado Pago',
          icon: 'ph-currency-circle-dollar',
          color: '#009EE3',
          description: 'Gateway de pagamento latino-americano',
          badge: null
        },
        {
          id: 4,
          category: 'payment',
          name: 'PagSeguro',
          icon: 'ph-credit-card',
          color: '#F8B334',
          description: 'Pagamentos online brasileiros',
          badge: null
        },

        // COMUNICAÇÃO
        {
          id: 5,
          category: 'communication',
          name: 'WhatsApp Business',
          icon: 'ph-whatsapp-logo',
          color: '#25D366',
          description: 'API oficial WhatsApp para atendimento',
          badge: 'Popular'
        },
        {
          id: 6,
          category: 'communication',
          name: 'Slack',
          icon: 'ph-slack-logo',
          color: '#4A154B',
          description: 'Notificações e alertas no Slack',
          badge: null
        },
        {
          id: 7,
          category: 'communication',
          name: 'Microsoft Teams',
          logo: 'https://img.icons8.com/color/96/microsoft-teams.png',
          description: 'Integração com MS Teams',
          badge: null
        },
        {
          id: 8,
          category: 'communication',
          name: 'Telegram',
          icon: 'ph-telegram-logo',
          color: '#0088CC',
          description: 'Notificações via Telegram',
          badge: null
        },

        // PRODUTIVIDADE
        {
          id: 9,
          category: 'productivity',
          name: 'Google Workspace',
          icon: 'ph-google-logo',
          color: '#4285F4',
          description: 'Drive, Sheets, Docs, Calendar integrados',
          badge: 'Popular'
        },
        {
          id: 10,
          category: 'productivity',
          name: 'Dropbox',
          logo: 'https://cdn.simpleicons.org/dropbox/0061FF',
          description: 'Armazenamento de arquivos na nuvem',
          badge: null
        },
        {
          id: 11,
          category: 'productivity',
          name: 'OneDrive',
          logo: 'https://img.icons8.com/color/96/microsoft-onedrive-2019.png',
          description: 'Sincronização com OneDrive',
          badge: null
        },
        {
          id: 12,
          category: 'productivity',
          name: 'Asana',
          icon: 'ph-check-square',
          color: '#F06A6A',
          description: 'Gestão de tarefas e projetos',
          badge: null
        },

        // MARKETING
        {
          id: 13,
          category: 'marketing',
          name: 'MailChimp',
          icon: 'ph-envelope-simple',
          color: '#FFE01B',
          description: 'Email marketing automatizado',
          badge: null
        },
        {
          id: 14,
          category: 'marketing',
          name: 'Facebook Leads',
          icon: 'ph-facebook-logo',
          color: '#1877F2',
          description: 'Importação automática de leads',
          badge: 'Popular'
        },
        {
          id: 15,
          category: 'marketing',
          name: 'Google Analytics',
          icon: 'ph-chart-line',
          color: '#E37400',
          description: 'Tracking e análise de conversões',
          badge: null
        },
        {
          id: 16,
          category: 'marketing',
          name: 'RD Station',
          icon: 'ph-megaphone',
          color: '#00A6A6',
          description: 'Automação de marketing',
          badge: null
        },

        // AUTOMAÇÃO
        {
          id: 17,
          category: 'automation',
          name: 'Zapier',
          icon: 'ph-lightning',
          color: '#FF4A00',
          description: 'Conecte 5000+ apps sem código',
          badge: 'Popular'
        },
        {
          id: 18,
          category: 'automation',
          name: 'Make (Integromat)',
          icon: 'ph-flow-arrow',
          color: '#6B3CF5',
          description: 'Automações visuais avançadas',
          badge: null
        },
        {
          id: 19,
          category: 'automation',
          name: 'n8n',
          icon: 'ph-git-fork',
          color: '#EA4B71',
          description: 'Automação self-hosted',
          badge: null
        },
        {
          id: 20,
          category: 'automation',
          name: 'Webhooks',
          icon: 'ph-git-branch',
          color: '#6D5DFC',
          description: 'Eventos customizados em tempo real',
          badge: null
        }
      ]
    }
  },
  computed: {
    filteredIntegrations() {
      if (this.activeCategory === 'all') {
        return this.integrations
      }
      return this.integrations.filter(i => i.category === this.activeCategory)
    }
  }
}
</script>

<style scoped>
/* ===== AREA BASE ===== */
.integrations-area {
  background: linear-gradient(180deg, #ffffff 0%, #f9f9f9 100%);
}

/* ===== CATEGORIAS ===== */
.integrations-categories {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 50px 0 40px;
}

.category-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 50px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.category-btn i {
  font-size: 18px;
}

.category-btn:hover {
  border-color: #6D5DFC;
  color: #6D5DFC;
  transform: translateY(-2px);
}

.category-btn.active {
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
  border-color: #6D5DFC;
  color: white;
  box-shadow: 0 4px 15px rgba(109, 93, 252, 0.3);
}

/* ===== GRID DE INTEGRAÇÕES ===== */
.integrations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 80px;
}

.integration-card {
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.06);
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  border: 2px solid transparent;
}

.integration-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  border-color: #6D5DFC;
}

.integration-logo {
  width: 70px;
  height: 70px;
  margin: 0 auto 20px;
  background: #f9f9f9;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.integration-card:hover .integration-logo {
  transform: scale(1.1);
  background: rgba(109, 93, 252, 0.1);
}

.integration-logo i {
  font-size: 36px;
}

.integration-card h4 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #1a1a1a;
}

.integration-card p {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

.integration-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(135deg, #FFB800 0%, #FF8C00 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

/* ===== RECURSOS DE INTEGRAÇÃO ===== */
.integration-features {
  margin-bottom: 60px;
}

.features-content h3 {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #1a1a1a;
}

.features-content p {
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
  line-height: 1.7;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 15px;
}

.features-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  color: #444;
}

.features-list i {
  font-size: 24px;
  color: #25D366;
  flex-shrink: 0;
}

/* ===== CODE PREVIEW ===== */
.features-image {
  position: relative;
}

.code-preview {
  background: #1a1a2e;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.code-header {
  background: #0f0f1a;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.code-lang {
  color: #C5BFFF;
  font-size: 13px;
  font-weight: 600;
}

.code-dots {
  display: flex;
  gap: 6px;
}

.code-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #666;
}

.code-content {
  padding: 25px;
  color: #E0E0E0;
  font-size: 13px;
  line-height: 1.8;
  font-family: 'Courier New', monospace;
  margin: 0;
  overflow-x: auto;
}

.code-content code {
  color: #E0E0E0;
}

.code-comment {
  color: #888;
}

/* ===== CTA ===== */
.integrations-cta {
  background: linear-gradient(135deg, rgba(109, 93, 252, 0.1) 0%, rgba(67, 97, 238, 0.1) 100%);
  padding: 60px 40px;
  border-radius: 20px;
  border: 2px solid rgba(109, 93, 252, 0.2);
}

.integrations-cta h3 {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 15px;
  color: #1a1a1a;
}

.integrations-cta p {
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
}

.btn-style-one {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 15px 35px;
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
  color: white;
  border-radius: 10px;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 2px solid #6D5DFC;
}

.btn-style-one:hover {
  background: linear-gradient(135deg, #5847E6 0%, #3651D8 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(109, 93, 252, 0.4);
  color: white;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 991px) {
  .integrations-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .integration-features {
    margin-bottom: 40px;
  }

  .features-content {
    margin-bottom: 40px;
  }
}

@media (max-width: 576px) {
  .integrations-grid {
    grid-template-columns: 1fr;
  }

  .integrations-categories {
    gap: 8px;
  }

  .category-btn {
    padding: 10px 18px;
    font-size: 13px;
  }

  .integrations-cta {
    padding: 40px 20px;
  }

  .integrations-cta h3 {
    font-size: 22px;
  }
}
.integration-logo img {
  width: 36px;
  height: 36px;
  object-fit: contain;
}
</style>